import {
  PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useExpandedItem } from "@/ui-components/expanded-items";

import {
  formatTime,
  getHeadlineCategoryById,
  headlinePublicUrl,
} from "../utils";
import Markdown, { type Components } from "react-markdown";
import rehypeRaw from "rehype-raw";

import { HighlightInfo } from "@/utils/portfolioUtils";
import {
  highlightTermsInText,
  SEARCH_HIGHLIGHT_COLOR,
} from "@/utils/text-highlight";
import { Headline } from "../models/Headline";
import { safeClipboardWrite } from "@/utils/clipboard";
import { useHasInternalNewsquawkUserRole } from "@/hooks/useHasInternalNewsquawkUserRole";
import {
  checkableTagIds,
  checkableTags,
} from "../../create-headline/models/HeadlineForm";
import { Tag, useTagManagerHydrate } from "@/shared/api/newsquawk-headlines";
import { trackContentView, trackContentEngagement } from "@/utils/gtm";

const ICON_BASE = "/images/icons";

type LegacyNewsquawkHeadlineItemProps = {
  headline: Headline;
  onItemEdit?: (headline: Headline) => void;
  onItemDelete?: (headline: Headline) => void;
  highlightInfo?: HighlightInfo;
  searchQuery?: string;
};

const isImportantHeadline = (tagIds: number[]) =>
  tagIds.includes(checkableTagIds.Important);

const isHighlightedHeadline = (tagIds: number[]) =>
  checkableTags.some(
    (tag) =>
      tagIds.includes(tag.id) &&
      tag.id !== checkableTagIds.Important &&
      (tag.id === checkableTagIds.Highlighted ||
        tag.id === checkableTagIds.Research ||
        tag.id === checkableTagIds.Flash)
  );

function LegacyNewsquawkHeadlineItem(props: LegacyNewsquawkHeadlineItemProps) {
  const { headline } = props;

  const [showDetails, toggleShowDetails] = useExpandedItem(
    headline.id,
    headline.show_details ?? false
  );

  const internalNewsquawkUser = useHasInternalNewsquawkUserRole();

  const headlineCategory = useMemo(() => {
    for (const tagId of headline.tag_id_list) {
      const category = getHeadlineCategoryById(Number(tagId));
      if (category) return category.title;
    }
    return undefined;
  }, [headline.tag_id_list]);

  const isImportant = useMemo(
    () => isImportantHeadline(headline.tag_id_list),
    [headline.tag_id_list]
  );

  const isHighlighted = useMemo(
    () => isHighlightedHeadline(headline.tag_id_list) || !!props.highlightInfo,
    [headline.tag_id_list, props.highlightInfo]
  );

  const hasAnalysis = isValidDetail(headline.analysis_details as string);
  const hasReaction = isValidDetail(headline.reaction_details as string);
  const hasExpandableContent =
    isValidDetail(headline.content) ||
    hasAnalysis ||
    hasReaction ||
    (headline.tags?.length ?? 0) > 0;

  // Track when details were opened for engagement time measurement
  const openedAtRef = useRef<number | null>(null);

  const toggleDetails = (item: Headline) => {
    if (!showDetails) {
      openedAtRef.current = Date.now();
      trackContentView({
        content_id: item.id,
        content_title: item.subject,
        content_type: "headline",
        content_category: headlineCategory ?? "uncategorized",
        content_source: item.source,
        content_tags: item.tags
          ?.map((t) => t.name)
          .filter(Boolean)
          .join(", "),
        content_widget: "headlines-legacy",
      });
    } else if (openedAtRef.current) {
      trackContentEngagement({
        content_id: item.id,
        content_title: item.subject,
        content_type: "headline",
        content_widget: "headlines-legacy",
        engagement_time_ms: Date.now() - openedAtRef.current,
      });
      openedAtRef.current = null;
    }

    // Expansion state is per widget instance (see useExpandedItem) — never
    // written onto the headline object, which every instance shares.
    toggleShowDetails();
  };

  const rowClass = [
    "lh-row",
    isImportant && "lh-important",
    isHighlighted && "lh-highlighted",
  ]
    .filter(Boolean)
    .join(" ");

  const statusIcon = isImportant ? "important" : "news";
  const statusTitle = isImportant ? "Important" : "News";

  const publishedLabel =
    typeof headline.published_at === "object"
      ? formatTime(new Date(headline.published_at.unix * 1000).toISOString())
      : new Date(headline.published_at as string).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
          hour12: false,
        });

  return (
    <div className={rowClass}>
      {/* Time */}
      <div className="lh-time">{publishedLabel}</div>

      {/* Status icon */}
      <div className="lh-status">
        <img
          src={`${ICON_BASE}/${statusIcon}.png`}
          alt={statusTitle}
          title={statusTitle}
        />
      </div>

      {/* Classification */}
      <div className="lh-class" title={headlineCategory}>
        {headlineCategory}
      </div>

      {/* Content */}
      <div className="lh-content">
        <button
          type="button"
          className="lh-headline-btn"
          onClick={() => toggleDetails(headline)}
        >
          <span className="lh-headline-text">
            {props.searchQuery?.trim() ? (
              <span
                dangerouslySetInnerHTML={{
                  __html: highlightTermsInText(
                    headline.subject,
                    props.searchQuery.trim().split(/\s+/).filter(Boolean),
                    SEARCH_HIGHLIGHT_COLOR
                  ),
                }}
              />
            ) : (
              <span
                style={{
                  backgroundColor: props.highlightInfo?.color,
                  color: props.highlightInfo ? "black" : undefined,
                }}
              >
                {headline.subject}
              </span>
            )}
            {props.highlightInfo && (
              <span style={{ color: props.highlightInfo.color }}>
                {` (${props.highlightInfo.portfolioName})`}
              </span>
            )}
          </span>

          <span className="lh-hints">
            {hasReaction && (
              <img
                src={`${ICON_BASE}/reaction.png`}
                alt="Contains market reaction info"
                title="Contains market reaction info"
              />
            )}
            {hasAnalysis && (
              <img
                src={`${ICON_BASE}/analysis.png`}
                alt="Contains analysis"
                title="Contains analysis"
              />
            )}
            {hasExpandableContent && (
              <img
                className="lh-toggle-icon"
                src={`${ICON_BASE}/${showDetails ? "collapse" : "expand"}.png`}
                alt={showDetails ? "Hide details" : "Expand details"}
                title={showDetails ? "Hide details" : "Expand details"}
              />
            )}
          </span>
        </button>

        {showDetails && (
          <LegacyHeadlineDetails
            headline={headline}
            highlightInfo={props.highlightInfo}
            searchQuery={props.searchQuery}
          >
            {internalNewsquawkUser && (
              <div className="lh-detail-actions">
                {props.onItemEdit && (
                  <button
                    type="button"
                    className="lh-action-btn"
                    onClick={() => props.onItemEdit?.(headline)}
                  >
                    Edit
                  </button>
                )}
                {props.onItemDelete && (
                  <button
                    type="button"
                    className="lh-action-btn lh-action-danger"
                    onClick={() => props.onItemDelete?.(headline)}
                  >
                    Delete
                  </button>
                )}
              </div>
            )}
          </LegacyHeadlineDetails>
        )}
      </div>
    </div>
  );
}

type LegacyHeadlineDetailsProps = {
  headline: Headline;
  highlightInfo?: HighlightInfo;
  searchQuery?: string;
} & PropsWithChildren;

const LegacyHeadlineDetails = ({
  headline,
  highlightInfo,
  searchQuery,
  children,
}: LegacyHeadlineDetailsProps) => {
  const [showCopied, setShowCopied] = useState<
    "headline" | "body" | "url" | null
  >(null);
  const internalNewsquawkUser = useHasInternalNewsquawkUserRole();

  const tagsToDisplay = useMemo(() => headline.tags || [], [headline.tags]);

  const { tags: displayTags, isLoading: tagsLoading } = useTagManagerHydrate(
    tagsToDisplay.length > 0 ? tagsToDisplay : null
  );

  const deduplicatedContent = useMemo(
    () => stripSourceFromContent(headline.content, headline.source),
    [headline.content, headline.source]
  );
  const deduplicatedLinkedContent = useMemo(
    () =>
      stripSourceFromContent(
        headline.linked_content as string,
        headline.source
      ),
    [headline.linked_content, headline.source]
  );

  const applyHighlight = (text: string | null | undefined): string => {
    if (!text) return "";
    let result = text;
    if (highlightInfo?.matchedTickers?.length) {
      result = highlightTermsInText(
        result,
        highlightInfo.matchedTickers,
        highlightInfo.color
      );
    }
    if (searchQuery?.trim()) {
      const searchTerms = searchQuery.trim().split(/\s+/).filter(Boolean);
      if (searchTerms.length > 0) {
        result = highlightTermsInText(result, searchTerms, SEARCH_HIGHLIGHT_COLOR);
      }
    }
    return result;
  };

  const handleHeadlineCopy = () => {
    setShowCopied("headline");
    safeClipboardWrite(headline.subject);
  };

  const handleBodyCopy = () => {
    setShowCopied("body");
    const sections: string[] = [];
    if (isValidDetail(deduplicatedContent)) sections.push(deduplicatedContent);
    if (isValidDetail(deduplicatedLinkedContent))
      sections.push(deduplicatedLinkedContent);
    if (isValidDetail((headline.analysis_details as string) ?? ""))
      sections.push(headline.analysis_details as string);
    if (isValidDetail(headline.reaction_details as string))
      sections.push(headline.reaction_details as string);
    if (isValidDetail(headline.source)) sections.push(headline.source);
    safeClipboardWrite(sections.join("\n\n"));
  };

  const handleUrlCopy = () => {
    setShowCopied("url");
    // Slug is derived from the raw stored subject, not the displayed text.
    safeClipboardWrite(headlinePublicUrl(headline.subject));
  };

  useEffect(() => {
    if (showCopied) {
      const timer = setTimeout(() => setShowCopied(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [showCopied]);

  const formatTimestamp = (
    timestamp: { text?: string; iso_8601?: string; unix?: number } | undefined
  ): string => {
    if (!timestamp) return "";
    if (timestamp.text) return timestamp.text;
    if (timestamp.iso_8601) return new Date(timestamp.iso_8601).toLocaleString();
    if (timestamp.unix) return new Date(timestamp.unix * 1000).toLocaleString();
    return "";
  };

  return (
    <div className="lh-details">
      {isValidDetail(deduplicatedContent) && (
        <Markdown
          skipHtml={false}
          rehypePlugins={[rehypeRaw]}
          components={markdownComponents}
        >
          {applyHighlight(deduplicatedContent)}
        </Markdown>
      )}

      {isValidDetail(deduplicatedLinkedContent) && (
        <Markdown
          skipHtml={false}
          rehypePlugins={[rehypeRaw]}
          components={markdownComponents}
        >
          {applyHighlight(deduplicatedLinkedContent)}
        </Markdown>
      )}

      {isValidDetail((headline.analysis_details as string) ?? "") && (
        <div className="lh-detail-section">
          <strong>Analysis Details</strong>
          <Markdown
            skipHtml={false}
            rehypePlugins={[rehypeRaw]}
            components={markdownComponents}
          >
            {applyHighlight(headline.analysis_details as string)}
          </Markdown>
          {headline.analysis_updated_at &&
            formatTimestamp(headline.analysis_updated_at) && (
              <p className="lh-detail-meta">
                Analysis last updated{" "}
                {formatTimestamp(headline.analysis_updated_at)}
              </p>
            )}
        </div>
      )}

      {isValidDetail(headline.reaction_details as string) && (
        <div className="lh-detail-section">
          <strong>Reaction Details</strong>
          <Markdown
            skipHtml={false}
            rehypePlugins={[rehypeRaw]}
            components={markdownComponents}
          >
            {applyHighlight(headline.reaction_details as string)}
          </Markdown>
          {headline.reaction_updated_at &&
            formatTimestamp(headline.reaction_updated_at) && (
              <p className="lh-detail-meta">
                Reaction last updated{" "}
                {formatTimestamp(headline.reaction_updated_at)}
              </p>
            )}
        </div>
      )}

      {isValidDetail(headline.source) && (
        <div className="lh-detail-section">
          <strong>Source </strong>
          <span
            dangerouslySetInnerHTML={{
              __html: ensureLinksOpenNewTab(applyHighlight(headline.source)),
            }}
          />
        </div>
      )}

      {/* Action buttons */}
      <div className="lh-detail-actions">
        <a
          href={createMailtoLink(headline.subject, headline.content)}
          target="_blank"
          rel="noopener noreferrer"
          className="lh-action-btn"
        >
          Email
        </a>
        <button type="button" className="lh-action-btn" onClick={handleHeadlineCopy}>
          {showCopied === "headline" ? "Copied!" : "Copy Headline"}
        </button>
        {isValidDetail(headline.content) && (
          <button type="button" className="lh-action-btn" onClick={handleBodyCopy}>
            {showCopied === "body" ? "Copied!" : "Copy Body"}
          </button>
        )}
        {/* Gated on the subject, not the payload's `slug`: the URL is derived
            from the subject, and the stored slug uses a different rule to the
            public site's. */}
        {internalNewsquawkUser && Boolean(headline.subject) && (
          <button type="button" className="lh-action-btn" onClick={handleUrlCopy}>
            {showCopied === "url" ? "Copied!" : "Copy Public URL"}
          </button>
        )}
      </div>

      {/* Tags */}
      {(displayTags.length > 0 || tagsLoading) && (
        <div className="lh-tags">
          {tagsLoading ? (
            <span className="lh-detail-meta">Loading tags...</span>
          ) : (
            displayTags.map((tag: Tag) => (
              <span key={tag.tag_id} className="lh-tag">
                {tag.name || "[Tag label not available]"}
              </span>
            ))
          )}
        </div>
      )}

      {children}
    </div>
  );
};

const isValidDetail = (detail: string | null | undefined): boolean =>
  !!detail && detail.trim().length > 0 && detail !== "<!---->";

/** Remove standalone lines from content that duplicate the source attribution. */
const stripSourceFromContent = (
  content: string | null | undefined,
  source: string | null | undefined
): string => {
  if (!content || !source) return content || "";
  const plainSource = source.replace(/<[^>]*>/g, "").trim();
  if (!plainSource) return content;
  const sourceLower = plainSource.toLowerCase();
  return content
    .split("\n")
    .filter((line) => {
      const stripped = line.trim().replace(/\*+/g, "").replace(/_+/g, "").trim();
      return stripped.toLowerCase() !== sourceLower;
    })
    .join("\n");
};

// Ensure all links open in a new tab
const markdownComponents: Components = {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  a: ({ node, ...rest }) => (
    <a {...rest} target="_blank" rel="noopener noreferrer" />
  ),
};

const ensureLinksOpenNewTab = (html: string): string =>
  html.replace(/<a\s/g, '<a target="_blank" rel="noopener noreferrer" ');

const createMailtoLink = (subject: string, plainContent: string) => {
  const mailSubject = encodeURIComponent(subject);
  const body = encodeURIComponent(
    `
Hey,

This just landed on my desk from Newsquawk - thought you should see it:

${subject}

${plainContent}

I can hop on the phone and discuss.

Thanks!
            `.trim()
  );
  return `mailto:?subject=${mailSubject}&body=${body}`;
};

export default LegacyNewsquawkHeadlineItem;
